/**
 * Comparator for the Kanban column order. The backend is the source of truth and
 * returns deals already in this order; this comparator exists only to place a
 * deal correctly when it is inserted into the cache optimistically (on a stage
 * change), before the refetch confirms the server order.
 *
 * Three groups, mirroring the backend and the card's activity status badge:
 *   0. No planned activity at all (the "yellow" cards) — first, newest first.
 *   1. Has an upcoming activity with a due date — earliest due date on top,
 *      tie-broken by closing chance (desc) and creation date (desc).
 *   2. Has a planned activity but no due date — last, newest first.
 *
 * The group is decided by plannedActivityCount, NOT by nextActivityDueAt alone.
 *
 * @param {Object} a - deal with plannedActivityCount, nextActivityDueAt, closingChance, createdAt
 * @param {Object} b - deal with plannedActivityCount, nextActivityDueAt, closingChance, createdAt
 * @returns {number} comparator result
 */
function dealOrderGroup(deal) {
	if ((deal.plannedActivityCount ?? 0) === 0) return 0; // yellow: no planned activity
	if (deal.nextActivityDueAt) return 1; // planned + dated
	return 2; // planned but undated
}

export function compareByNextActivityDueAt(a, b) {
	const aGroup = dealOrderGroup(a);
	const bGroup = dealOrderGroup(b);
	if (aGroup !== bGroup) return aGroup - bGroup;

	if (aGroup === 1) {
		const aDue = new Date(a.nextActivityDueAt).getTime();
		const bDue = new Date(b.nextActivityDueAt).getTime();
		if (aDue !== bDue) return aDue - bDue;
		const aScore = a.closingChance ?? -1;
		const bScore = b.closingChance ?? -1;
		if (aScore !== bScore) return bScore - aScore;
	}

	return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
}
